import { createApi } from '@reduxjs/toolkit/query/react';
import { urls } from '../../utils/helpers/ApiClient';
import axiosFetchBase from '../axiosFetchBase';

export const ingProjectsApi = createApi({
  reducerPath: 'ingProjectsApi',
  baseQuery: axiosFetchBase,
  tagTypes: [
    'IngProjects',
    'IngProjectsMyApplications',
    'IngProjectsAssigned',
    'IngProjectsStudentStatus',
  ],
  endpoints: (builder) => ({
    getIngProjects: builder.query({
      query: () => ({
        url: urls.ingProjects,
      }),
      providesTags: ['IngProjects'],
    }),

    getPublicIngProjects: builder.query({
      query: () => ({
        url: urls.ingProjectsPublic,
      }),
    }),

    getPublicIngProject: builder.query({
      query: (id) => ({
        url: urls.ingProjectsPublicDetail(id),
      }),
    }),

    getIngProject: builder.query({
      query: (id) => ({
        url: urls.ingProjectsDetail(id),
      }),
      providesTags: (result, error, id) => [{ type: 'IngProjects', id }],
    }),

    getIngProjectStudents: builder.query({
      query: (id) => ({
        url: urls.ingProjectsStudents(id),
      }),
      providesTags: (result, error, id) => [
        { type: 'IngProjects', id: `students-${id}` },
      ],
    }),

    saveIngProject: builder.mutation({
      query(req) {
        if (req.projectId) {
          return {
            url: urls.ingProjectsDetail(req.projectId),
            method: 'PUT',
            data: req.body,
          };
        } else {
          return {
            url: urls.ingProjects,
            method: 'POST',
            data: req.body,
          };
        }
      },
      invalidatesTags: ['IngProjects'],
    }),

    deleteIngProject: builder.mutation({
      query(id) {
        return {
          url: urls.ingProjectsDetail(id),
          method: 'DELETE',
        };
      },
      invalidatesTags: ['IngProjects'],
    }),

    getMyApplications: builder.query({
      query: () => ({
        url: urls.ingProjectsMyApplications,
      }),
      providesTags: ['IngProjectsMyApplications'],
    }),

    getAssignedProjects: builder.query({
      query: () => ({
        url: urls.ingProjectsAssigned,
      }),
      providesTags: ['IngProjectsAssigned'],
    }),

    updateApplicationTeacherPriority: builder.mutation({
      query({ applicationId, teacherPriority }) {
        return {
          url: urls.ingProjectsApplicationTeacherPriority(applicationId),
          method: 'PUT',
          data: { teacher_priority: teacherPriority },
        };
      },
      invalidatesTags: ['IngProjects', 'IngProjectsMyApplications'],
    }),

    archiveIngProject: builder.mutation({
      query({ projectId, archive }) {
        return {
          url: urls.ingProjectsArchive(projectId),
          method: 'PUT',
          data: { archive },
        };
      },
      invalidatesTags: ['IngProjects'],
    }),

    approveIngProject: builder.mutation({
      query(projectId) {
        return {
          url: urls.ingProjectsApprove(projectId),
          method: 'PUT',
        };
      },
      invalidatesTags: ['IngProjects'],
    }),

    rejectIngProject: builder.mutation({
      query(projectId) {
        return {
          url: urls.ingProjectsReject(projectId),
          method: 'PUT',
        };
      },
      invalidatesTags: ['IngProjects'],
    }),

    createApplication: builder.mutation({
      query(body) {
        return {
          url: urls.ingProjectsApplications,
          method: 'POST',
          data: body,
        };
      },
      invalidatesTags: ['IngProjectsMyApplications'],
    }),

    updateApplication: builder.mutation({
      query({ applicationId, body }) {
        return {
          url: urls.ingProjectsApplicationDetail(applicationId),
          method: 'PUT',
          data: body,
        };
      },
      invalidatesTags: ['IngProjectsMyApplications'],
    }),

    deleteApplication: builder.mutation({
      query(applicationId) {
        return {
          url: urls.ingProjectsApplicationDetail(applicationId),
          method: 'DELETE',
        };
      },
      invalidatesTags: ['IngProjectsMyApplications'],
    }),

    getStudentStatus: builder.query({
      query: () => ({
        url: urls.ingProjectsStudentStatus,
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['IngProjectsStudentStatus'],
    }),

    lockStudent: builder.mutation({
      query() {
        return {
          url: urls.ingProjectsStudentLock,
          method: 'POST',
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),

    getLockedStudents: builder.query({
      query: () => ({
        url: urls.ingProjectsStudentsLocked,
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['IngProjectsStudentStatus'],
    }),

    unlockStudent: builder.mutation({
      query({ studentIds, resetProjects }) {
        return {
          url: urls.ingProjectsStudentUnlock,
          method: 'POST',
          data: { student_ids: studentIds, reset_projects: resetProjects },
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),

    getActiveStudents: builder.query({
      query: (filter = 'current') => ({
        url: urls.ingProjectsStudentsActive,
        params: { filter },
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['IngProjectsStudentStatus'],
    }),

    getAcceptedStudents: builder.query({
      query: () => ({
        url: urls.ingProjectsAcceptedStudents,
      }),
      transformResponse: (response) =>
        (response?.data ?? response)?.map((student) => {
          const assignedProject = (
            student.student?.ip_project_students ?? []
          ).find(
            (projectStudent) =>
              Number(projectStudent.year) === Number(student.year),
          );

          return {
            id: student.id,
            student_id: student.student_id ?? student.student?.id ?? null,
            first_name: student.student?.person?.first_name ?? null,
            last_name: student.student?.person?.last_name ?? null,
            full_name: student.student?.person?.full_name ?? null,
            study_group: student.student?.school_group ?? null,
            study_program:
              student.studyProgram?.shortcut ??
              student.study_program_shortcut ??
              null,
            average_grade: student.average_grade ?? null,
            accepted_year: student.year ?? null,
            assigned_project_name: assignedProject?.project?.name ?? null,
          };
        }) ?? [],
      providesTags: ['IngProjectsStudentStatus'],
    }),

    deleteAcceptedStudent: builder.mutation({
      query(id) {
        return {
          url: urls.ingProjectsAcceptedStudentDetail(id),
          method: 'DELETE',
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),

    getStudentApplications: builder.query({
      query: (studentId) => ({
        url: urls.ingProjectsStudentApplications(studentId),
      }),
      transformResponse: (response) => response?.data ?? response,
    }),

    getActiveStudent: builder.query({
      query: (id) => ({
        url: urls.ingProjectsStudentActive(id),
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['IngProjectsStudentStatus'],
    }),

    updateActiveStudentAssignment: builder.mutation({
      query({ studentId, body }) {
        return {
          url: urls.ingProjectsStudentActive(studentId),
          method: 'PUT',
          data: body,
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),

    getStudentsWithoutProjects: builder.query({
      query: () => ({
        url: urls.ingProjectsStudentsWithoutProjects,
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['IngProjectsStudentStatus'],
    }),

    createActiveStudentAssignment: builder.mutation({
      query(body) {
        return {
          url: urls.ingProjectsStudentActive(),
          method: 'POST',
          data: body,
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),

    deleteActiveStudentAssignment: builder.mutation({
      query(studentId) {
        return {
          url: urls.ingProjectsStudentActive(studentId),
          method: 'DELETE',
        };
      },
      invalidatesTags: ['IngProjectsStudentStatus'],
    }),
  }),
});

export const {
  useGetIngProjectsQuery,
  useGetPublicIngProjectsQuery,
  useGetPublicIngProjectQuery,
  useGetIngProjectQuery,
  useGetIngProjectStudentsQuery,
  useSaveIngProjectMutation,
  useDeleteIngProjectMutation,
  useGetMyApplicationsQuery,
  useGetAssignedProjectsQuery,
  useUpdateApplicationTeacherPriorityMutation,
  useArchiveIngProjectMutation,
  useApproveIngProjectMutation,
  useRejectIngProjectMutation,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
  useGetStudentStatusQuery,
  useLockStudentMutation,
  useGetLockedStudentsQuery,
  useUnlockStudentMutation,
  useGetActiveStudentsQuery,
  useGetAcceptedStudentsQuery,
  useDeleteAcceptedStudentMutation,
  useGetStudentApplicationsQuery,
  useGetActiveStudentQuery,
  useUpdateActiveStudentAssignmentMutation,
  useGetStudentsWithoutProjectsQuery,
  useCreateActiveStudentAssignmentMutation,
  useDeleteActiveStudentAssignmentMutation,
} = ingProjectsApi;
